import { useRef } from "react";
import { Link } from "react-router-dom";
import { Play, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { ACCESS_LABEL } from "@/lib/api";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <div className={`reveal ${className}`} style={{ animationDelay: `${Math.min(delay, 0.4)}s` }}>{children}</div>
);

export const AccessBadge = ({ access, locked }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${access === "public" ? "border-white/20 text-slate-200 bg-black/50" : "border-[#D4AF37]/50 text-[#F1C40F] bg-black/80"}`} data-testid="access-badge">
    {locked && <Lock size={10} />}{ACCESS_LABEL[access] || "Free"}
  </span>
);

export const DemoTag = ({ show }) => show ? <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-brandred/80 text-white">Demo</span> : null;

export const VideoCard = ({ v, wide }) => (
  <Link to={`/videos/${v.slug}`} className={`group relative shrink-0 ${wide ? "w-full" : "w-[270px] sm:w-[320px]"} snap-start`} data-testid={`video-card-${v.slug}`}>
    <div className="relative aspect-video rounded-lg overflow-hidden border border-white/10 group-hover:border-[#D4AF37]/60 transition-[border-color] duration-300">
      <img src={v.thumbnail} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      <div className="absolute top-3 left-3 flex gap-2"><AccessBadge access={v.access} locked={v.locked} /><DemoTag show={v.is_demo} /></div>
      <span className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-[#D4AF37] text-[#050811] flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-[opacity,transform] duration-300">
        {v.locked ? <Lock size={20} /> : <Play size={22} className="ml-1" fill="currentColor" />}
      </span>
      {v.duration && <span className="absolute bottom-3 right-3 text-[11px] text-slate-200 bg-black/60 px-2 py-0.5 rounded">{v.duration}</span>}
    </div>
    <p className="mt-3 text-sm font-medium text-slate-100 group-hover:text-gold-light transition-colors line-clamp-2">{v.title}</p>
    <p className="text-xs text-slate-500 mt-1">{v.category}</p>
  </Link>
);

export const VideoRow = ({ title, videos, testid }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  if (!videos?.length) return null;
  return (
    <section className="mb-14" data-testid={testid}>
      <div className="flex items-end justify-between mb-5">
        <h3 className="font-serif text-xl sm:text-2xl text-white">{title}</h3>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} aria-label="Scroll left" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#D4AF37] text-white"><ChevronLeft size={16} /></button>
          <button onClick={() => scroll(1)} aria-label="Scroll right" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#D4AF37] text-white"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2">
        {videos.map((v) => <VideoCard key={v.id} v={v} />)}
      </div>
    </section>
  );
};

export const PageHero = ({ eyebrow, title, sub, image, children }) => (
  <section className="relative pt-40 pb-20 overflow-hidden grain">
    {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
    <div className="absolute inset-0 bg-gradient-to-b from-[#050811]/60 via-[#050811]/85 to-[#050811]" />
    <div className="relative container-x">
      <Reveal>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1 className="h1 max-w-4xl">{title}</h1>
        {sub && <p className="lead mt-6 max-w-2xl">{sub}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const Empty = ({ text }) => <div className="card-lux p-12 text-center text-slate-400" data-testid="empty-state">{text}</div>;
